import React from 'react';
import { Box, Container, Typography, Grid, Card, Paper, Button, Link as MuiLink } from '@mui/material';
import {
  Headset,
  AccessTime,
  Person,
  Email,
  Phone,
  ScreenShare,
  CheckCircle,
  Cancel,
  Help
} from '@mui/icons-material';
import Navigation from './Navigation';
import { supportContent } from '../content/pages/support';

// Icon mapping
const IconMap: Record<string, React.ReactElement> = {
  Headset: <Headset sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />,
  AccessTime: <AccessTime sx={{ fontSize: 64, color: 'primary.main', mb: 3 }} />,
  Person: <Person sx={{ fontSize: 64, color: 'primary.main', mb: 3 }} />,
  Email: <Email sx={{ fontSize: 48, mb: 1 }} />,
  Help: <Help sx={{ fontSize: 48, mb: 1 }} />,
  ScreenShare: <ScreenShare sx={{ fontSize: 48, mb: 1 }} />,
  // Composite icon for MultipleChannels card
  MultipleChannels: (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
      <Email sx={{ fontSize: 48, color: 'primary.main' }} />
      <Phone sx={{ fontSize: 48, color: 'primary.main' }} />
      <ScreenShare sx={{ fontSize: 48, color: 'primary.main' }} />
    </Box>
  )
};

const Support: React.FC = () => {
  const c = supportContent;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        {/* Navigation */}
        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            {IconMap.Headset}
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', mb: 4 }}>
              {c.hero.subtitle}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 700, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
              {c.hero.description}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Support Promise Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            {c.promise.title}
          </Typography>
          <Typography variant="h6" sx={{ color: "#5A5856", maxWidth: 800, mx: 'auto' }}>
            {c.promise.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {c.promise.cards.map((card, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, boxShadow: 2 }}>
                {IconMap[card.iconName]}
                <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  {card.title}
                </Typography>
                <Typography variant="body1" sx={{ color: "#5A5856", mb: 3 }}>
                  {card.description}
                </Typography>
                {card.footer && (
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {card.footer}
                  </Typography>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* What We Support Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              {c.scope.title}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, bgcolor: 'background.paper', height: '100%' }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  {c.scope.included.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {c.scope.included.items.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Typography variant="body1">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, bgcolor: 'secondary.light', height: '100%' }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  {c.scope.excluded.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {c.scope.excluded.items.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Cancel sx={{ color: '#ed6c02', mt: 0.5 }} />
                      <Typography variant="body1">{item}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {c.scope.excluded.footer.text}
                    <MuiLink href={c.scope.excluded.footer.linkUrl} color="primary" sx={{ fontWeight: 600 }}>
                      {c.scope.excluded.footer.linkText}
                    </MuiLink>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Support Philosophy Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
            {c.philosophy.title}
          </Typography>

          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {c.philosophy.whyHumanFirst.title}
            </Typography>

            {c.philosophy.whyHumanFirst.paragraphs.map((para, i) => (
              <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
                {para}
              </Typography>
            ))}

            <Box sx={{ pl: 3, mb: 4 }}>
              {c.philosophy.whyHumanFirst.points.map((point, i) => (
                <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 2 }}>
                  <strong>{point.strong}</strong>{point.text}
                </Typography>
              ))}
            </Box>

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2, mt: 6 }}>
              {c.philosophy.asWeGrow.title}
            </Typography>

            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
              {c.philosophy.asWeGrow.text}
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Contact Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
              {c.help.title}
            </Typography>

            <Grid container spacing={3} sx={{ mb: 6 }}>
              {c.help.channels.map((channel, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Box sx={{ mb: 2 }}>
                    {IconMap[channel.iconName]}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {channel.title}
                    </Typography>
                    {channel.linkUrl ? (
                      <MuiLink href={channel.linkUrl} target={channel.linkUrl.startsWith('http') ? '_blank' : undefined} color="inherit" sx={{ fontSize: '1.1rem' }}>
                        {channel.linkText}
                      </MuiLink>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {channel.subtext}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{ p: 4, bgcolor: 'background.paper', color: '#2D2C2A' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                {c.help.faq.title}
              </Typography>

              <Box sx={{ textAlign: 'left' }}>
                {c.help.faq.items.map((item, i) => (
                  <React.Fragment key={i}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                      {item.question}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {item.answer}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Paper>

            <Button
              variant="outlined"
              size="large"
              href="mailto:support@brandcast.app"
              sx={{
                mt: 4,
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 600,
                color: 'white',
                borderColor: 'white',
                borderWidth: 2,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderWidth: 2
                }
              }}
            >
              {c.help.cta}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Support;
